import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, LogIn, LogOut, Link, Cloud, Smartphone } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const AuthButton: React.FC = () => {
  const { user, signInWithGoogle, signOut, linkWithGoogle, isAnonymous, isAuthenticated } = useAuth();
  const [showMenu, setShowMenu] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      setShowMenu(false);
    } catch (error) {
    }
  };

  const handleLinkAccount = async () => {
    try {
      await linkWithGoogle();
      setShowMenu(false);
    } catch (error) {
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowMenu(false);
    } catch (error) {
    }
  };

  if (!isAuthenticated) {
    return (
      <motion.button
        onClick={handleGoogleSignIn}
        className="fixed top-24 right-6 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg border border-slate-200 hover:bg-white transition-colors"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <LogIn size={20} className="text-slate-600" />
      </motion.button>
    );
  }

  return (
    <div className="fixed top-24 right-6">
      <motion.button
        onClick={() => setShowMenu(!showMenu)}
        className="bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg border border-slate-200 hover:bg-white transition-colors"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {user?.photoURL ? (
          <img
            src={user.photoURL}
            alt="Profile"
            className="w-6 h-6 rounded-full"
          />
        ) : (
          <User size={20} className="text-slate-600" />
        )}
      </motion.button>

      <AnimatePresence>
        {showMenu && (
          <motion.div
            className="absolute top-14 right-0 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden min-w-64"
            initial={{ opacity: 0, scale: 0.8, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -10 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* User Info */}
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                {user?.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt="Profile"
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                    <User size={20} className="text-slate-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">
                    {user?.displayName || 'Anonymous Player'}
                  </p>
                  <div className="flex items-center gap-1 text-sm text-slate-500">
                    {isAnonymous ? (
                      <>
                        <Smartphone size={14} />
                        <span>Local only</span>
                      </>
                    ) : (
                      <>
                        <Cloud size={14} />
                        <span>Synced</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              {isAnonymous && (
                <motion.button
                  onClick={handleLinkAccount}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                  whileHover={{ backgroundColor: "#f8fafc" }}
                >
                  <Link size={18} className="text-blue-600" />
                  <div>
                    <p className="font-medium text-slate-800">Link Google Account</p>
                    <p className="text-sm text-slate-500">Save progress across devices</p>
                  </div>
                </motion.button>
              )}

              <motion.button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                whileHover={{ backgroundColor: "#f8fafc" }}
              >
                <LogOut size={18} className="text-slate-600" />
                <span className="font-medium text-slate-800">Sign Out</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {showMenu && (
          <motion.div
            className="fixed inset-0 z-[-1]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowMenu(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AuthButton;